import { useEffect, useRef } from "react";
import {
  Animated,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import {
  ExternalLink,
  Globe,
  MapPin,
  Phone,
  Snowflake,
  Truck,
  X,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  MOCK_PHARMACY_DIRECTORY,
  MOCK_PHARMACY_MEDICATIONS,
  usePharmacy,
  type PharmacyDirectoryType,
} from "@src/packages/providers";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { IconButton } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<AccessStackParamList, "PharmacyDetail">;

const TYPE_LABEL_KEYS: Record<PharmacyDirectoryType, string> = {
  specialty: "access.pharmacy.type.specialty",
  mail_order: "access.pharmacy.type.mail_order",
  retail: "access.pharmacy.type.retail",
};

const SPECIALTY_LABELS: Record<string, string> = {
  "trumprx-participating": "TrumpRx",
};

function formatSpecialty(value: string) {
  if (SPECIALTY_LABELS[value]) return SPECIALTY_LABELS[value];

  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Match a patient prescription against a pharmacy's published price list.
 * Brand match is required; strength and dose-unit narrow when the brand
 * has multiple SKUs (e.g., Gonal-F 300 vs 450 vs 900 IU).
 */
function matchPublishedPrice(
  patientName: string | undefined,
  patientDose: string,
  patientDoseUnit: string | undefined,
  items: { medication: string; price: number }[],
) {
  if (!patientName) return undefined;
  const brandToken = patientName.trim().toLowerCase().split(/[\s(]/)[0];
  if (!brandToken) return undefined;
  const brandMatches = items.filter((item) =>
    item.medication.toLowerCase().includes(brandToken),
  );
  if (brandMatches.length === 0) return undefined;
  if (brandMatches.length === 1) return brandMatches[0];
  const doseNarrowed = patientDose
    ? brandMatches.filter((item) => item.medication.includes(patientDose))
    : brandMatches;
  if (doseNarrowed.length === 1) return doseNarrowed[0];
  const unitNarrowed =
    patientDoseUnit && doseNarrowed.length > 1
      ? doseNarrowed.filter((item) =>
          item.medication.toLowerCase().includes(patientDoseUnit.toLowerCase()),
        )
      : doseNarrowed;
  return unitNarrowed[0] ?? doseNarrowed[0] ?? brandMatches[0];
}

function getDemoMedicationPrice(
  pharmacy: {
    demo?: {
      medicationPrices?: { medicationId: string; price: number | null }[];
    };
  },
  medicationId: string,
) {
  return (
    pharmacy.demo?.medicationPrices?.find(
      (entry) => entry.medicationId === medicationId,
    )?.price ?? null
  );
}

function DirectoryState({ title, body }: { title: string; body: string }) {
  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        padding: 20,
        gap: 8,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.text, fontSize: 17, fontWeight: "700" }}
      >
        {title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.textLight, fontSize: 14, lineHeight: 20 }}
      >
        {body}
      </Text>
    </View>
  );
}

function ContactRow({
  icon: Icon,
  label,
  onPress,
}: {
  icon: LucideIcon;
  label: string;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("ContactRow");
  const content = (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
      <Icon size={17} color={EMBER.colors.warm} strokeWidth={1.6} />
      <Text
        className="font-dm-sans"
        numberOfLines={2}
        style={{
          flex: 1,
          color: onPress ? EMBER.colors.warm : EMBER.colors.textLight,
          fontSize: 14,
          fontWeight: onPress ? "600" : "400",
        }}
      >
        {label}
      </Text>
      {onPress ? <ExternalLink size={14} color={EMBER.colors.warm} /> : null}
    </View>
  );

  return onPress ? (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={() => {
        capturePress(pressLabel(label), "pharmacy_contact_row");
        onPress();
      }}
      style={{ paddingVertical: 4 }}
    >
      {content}
    </Pressable>
  ) : (
    content
  );
}

export default function PharmacyDetailScreen({ route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacyDetailScreen");
  const { locale } = useLocale();
  const { pharmacyId, demo, returnTab } = route.params;
  const demoMode =
    demo === true ||
    (Platform.OS === "web" &&
      typeof window !== "undefined" &&
      ["1", "true"].includes(
        new URLSearchParams(window.location.search).get("demo") ?? "",
      ));
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Pharmacy",
  });
  const {
    data: pharmacy,
    source,
    isLoading,
    error,
  } = usePharmacy(pharmacyId, {
    demo: demoMode,
  });
  const { data: patientMedications = [] } = usePatientMedications();
  const scale = useRef(new Animated.Value(0.96)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1,
        friction: 8,
        tension: 65,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start();
  }, [opacity, scale]);

  const openWebsite = () => {
    if (pharmacy?.website) void Linking.openURL(pharmacy.website);
  };

  const callPharmacy = () => {
    if (pharmacy?.phone) {
      void Linking.openURL(`tel:${pharmacy.phone.replace(/[^\d+]/g, "")}`);
    }
  };

  const openQuoteSource = () => {
    const url = pharmacy?.quoteUrl ?? pharmacy?.sourceUrl ?? pharmacy?.website;
    if (url) void Linking.openURL(url);
  };

  const medicationSummary = patientMedications
    .filter(
      (medication) =>
        medication.medication?.name || medication.rawMedicationName,
    )
    .map((medication, index) => {
      const name = medication.medication?.name ?? medication.rawMedicationName;
      const doseValue = medication.dose ? String(medication.dose) : "";
      const doseLabel = medication.dose
        ? `${medication.dose} ${medication.doseUnit ?? ""}`.trim()
        : "";
      const label = [name, doseLabel].filter(Boolean).join(" · ");
      const matchedPrice = pharmacy?.publishedPrices
        ? matchPublishedPrice(
            name ?? undefined,
            doseValue,
            medication.doseUnit ?? undefined,
            pharmacy.publishedPrices.items,
          )
        : undefined;
      return {
        key: `${medication.id}-${index}`,
        label,
        matchedPrice,
      };
    });

  // Dedupe by matched Mandell's SKU — a patient may log the same drug many
  // times in their calendar; only show one row per unique SKU.
  const matchedMedications = (() => {
    const seen = new Set<string>();
    const rows: (typeof medicationSummary)[number][] = [];
    for (const m of medicationSummary) {
      if (!m.matchedPrice) continue;
      const key = m.matchedPrice.medication;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(m);
    }
    return rows;
  })();
  const publishedPriceTotal = matchedMedications.reduce(
    (sum, m) => sum + (m.matchedPrice?.price ?? 0),
    0,
  );

  const medicationPriceRows =
    source === "demo" && pharmacy
      ? MOCK_PHARMACY_MEDICATIONS.map((medication) => {
          const currentPrice = getDemoMedicationPrice(pharmacy, medication.id);
          const lowerElsewhere =
            currentPrice == null
              ? undefined
              : MOCK_PHARMACY_DIRECTORY.filter(
                  (candidate) => candidate.id !== pharmacy.id,
                )
                  .map((candidate) => ({
                    pharmacy: candidate,
                    price: getDemoMedicationPrice(candidate, medication.id),
                  }))
                  .filter(
                    (alternative) =>
                      alternative.price != null &&
                      alternative.price < currentPrice,
                  )
                  .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))[0];

          return { currentPrice, lowerElsewhere, medication };
        })
      : [];
  const lowerPriceCount = medicationPriceRows.filter(
    (row) => row.lowerElsewhere,
  ).length;

  return (
    <Animated.View
      style={{
        flex: 1,
        opacity,
        transform: [{ scale }],
      }}
    >
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            width: "100%",
            maxWidth: 520,
            alignSelf: "center",
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP,
            paddingBottom: 100,
          }}
          showsVerticalScrollIndicator={false}
        >
          <IconButton
            onPress={handleBack}
            hitSlop={8}
            size={32}
            surface="soft"
            backgroundColor="rgba(0,0,0,0.06)"
            pressedBackgroundColor="rgba(0,0,0,0.06)"
            pressedOpacity={1}
            accessibilityLabel={t("access.back")}
            style={{ alignSelf: "flex-end", marginBottom: 12 }}
          >
            <X size={16} color={EMBER.colors.textMid} />
          </IconButton>

          {isLoading ? (
            <DirectoryState
              title={t("access.pharmacy.directory_loading_title")}
              body={t("access.pharmacy.directory_loading_body")}
            />
          ) : error || !pharmacy ? (
            <DirectoryState
              title={t("access.pharmacy.directory_error_title")}
              body={t("access.pharmacy.directory_error_body")}
            />
          ) : (
            <View
              style={{
                ...(GLASS_CARD_STYLE as object),
                gap: 24,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              <View style={{ gap: 8 }}>
                <View
                  style={{
                    alignSelf: "flex-start",
                    paddingHorizontal: 8,
                    paddingVertical: 2,
                    borderRadius: 6,
                    backgroundColor: "rgba(139, 111, 94, 0.1)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: "#6B5548",
                      fontSize: 11,
                      fontWeight: "700",
                    }}
                  >
                    {t(TYPE_LABEL_KEYS[pharmacy.type])}
                  </Text>
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.text,
                    fontSize: 28,
                    fontWeight: "700",
                    letterSpacing: -0.5,
                  }}
                >
                  {pharmacy.name}
                </Text>
              </View>

              <View style={{ gap: 12 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.textMid,
                    fontSize: 13,
                    fontWeight: "600",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t("access.pharmacy.contact_section_title")}
                </Text>
                {pharmacy.phone ? (
                  <ContactRow
                    icon={Phone}
                    label={pharmacy.phone}
                    onPress={callPharmacy}
                  />
                ) : (
                  <ContactRow
                    icon={Phone}
                    label={t("access.pharmacy.phone_unverified")}
                  />
                )}
                {pharmacy.website ? (
                  <ContactRow
                    icon={Globe}
                    label={pharmacy.website.replace(/^https?:\/\//, "")}
                    onPress={openWebsite}
                  />
                ) : null}
                {pharmacy.city || pharmacy.state ? (
                  <ContactRow
                    icon={MapPin}
                    label={[pharmacy.city, pharmacy.state, pharmacy.country]
                      .filter(Boolean)
                      .join(", ")}
                  />
                ) : null}
              </View>

              <View style={{ gap: 10 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.textMid,
                    fontSize: 13,
                    fontWeight: "600",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {t("access.pharmacy.services_section_title")}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {pharmacy.compoundingOnSite ? (
                    <View
                      key="compounding-on-site"
                      style={{
                        paddingHorizontal: 9,
                        paddingVertical: 5,
                        borderRadius: 999,
                        backgroundColor: "rgba(139, 111, 94, 0.1)",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: "#6B5548",
                          fontSize: 11,
                          fontWeight: "600",
                        }}
                      >
                        {t("access.pharmacy.in_house_compounding")}
                      </Text>
                    </View>
                  ) : null}
                  {pharmacy.specialties
                    .filter((specialty) => specialty !== "fertility")
                    .map((specialty) => (
                      <View
                        key={specialty}
                        style={{
                          paddingHorizontal: 9,
                          paddingVertical: 5,
                          borderRadius: 999,
                          backgroundColor: "rgba(139, 111, 94, 0.1)",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            color: "#6B5548",
                            fontSize: 11,
                            fontWeight: "600",
                          }}
                        >
                          {formatSpecialty(specialty)}
                        </Text>
                      </View>
                    ))}
                </View>
              </View>

              {source === "demo" && pharmacy.demo ? (
                <View
                  style={{
                    gap: 6,
                    padding: 16,
                    borderRadius: 16,
                    backgroundColor: "rgba(139, 111, 94, 0.08)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.warm,
                      fontSize: 11,
                      fontWeight: "700",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("access.pharmacy.demo_quote_label")}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.text,
                      fontSize: 24,
                      fontWeight: "700",
                    }}
                  >
                    {pharmacy.demo.totalPrice == null
                      ? t("access.pharmacy.contact_for_pricing")
                      : `$${pharmacy.demo.totalPrice.toLocaleString("en-US")}`}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textLight,
                      fontSize: 12,
                      lineHeight: 18,
                    }}
                  >
                    {t("access.pharmacy.demo_detail_quote_body")}
                  </Text>
                </View>
              ) : null}

              {source === "demo" && pharmacy.demo?.medicationPrices ? (
                <View style={{ gap: 12 }}>
                  <View style={{ gap: 5 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textMid,
                        fontSize: 13,
                        fontWeight: "600",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("access.pharmacy.medication_prices_section_title")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 13,
                        lineHeight: 19,
                      }}
                    >
                      {t("access.pharmacy.medication_prices_section_body")}
                    </Text>
                  </View>

                  <View style={{ gap: 8 }}>
                    {medicationPriceRows.map(
                      ({ currentPrice, lowerElsewhere, medication }) => (
                        <View
                          key={medication.id}
                          style={{
                            gap: 7,
                            padding: 12,
                            borderRadius: 14,
                            backgroundColor: "rgba(139, 111, 94, 0.06)",
                          }}
                        >
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <View style={{ flex: 1, gap: 2 }}>
                              <Text
                                className="font-dm-sans"
                                style={{
                                  color: EMBER.colors.text,
                                  fontSize: 14,
                                  fontWeight: "700",
                                }}
                              >
                                {medication.name}
                              </Text>
                              <Text
                                className="font-dm-sans"
                                style={{
                                  color: EMBER.colors.textLight,
                                  fontSize: 11,
                                }}
                              >
                                {medication.dosage}
                              </Text>
                            </View>
                            <Text
                              className="font-dm-sans"
                              style={{
                                color: EMBER.colors.text,
                                fontSize: 16,
                                fontWeight: "800",
                              }}
                            >
                              {currentPrice == null
                                ? t("access.pharmacy.medication_price_contact")
                                : `$${currentPrice.toLocaleString("en-US")}`}
                            </Text>
                          </View>
                          {lowerElsewhere ? (
                            <Text
                              className="font-dm-sans"
                              style={{
                                alignSelf: "flex-start",
                                paddingHorizontal: 7,
                                paddingVertical: 3,
                                borderRadius: 999,
                                backgroundColor: `${EMBER.colors.green}16`,
                                color: EMBER.colors.green,
                                fontSize: 10,
                                fontWeight: "700",
                              }}
                            >
                              {t(
                                "access.pharmacy.medication_price_lower_elsewhere",
                                {
                                  params: {
                                    amount: (
                                      (currentPrice ?? 0) -
                                      (lowerElsewhere.price ?? 0)
                                    ).toLocaleString("en-US"),
                                    pharmacy: lowerElsewhere.pharmacy.name,
                                  },
                                },
                              )}
                            </Text>
                          ) : null}
                        </View>
                      ),
                    )}
                  </View>

                  {lowerPriceCount > 0 ? (
                    <View
                      style={{
                        gap: 5,
                        padding: 14,
                        borderRadius: 16,
                        backgroundColor: `${EMBER.colors.green}12`,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.text,
                          fontSize: 14,
                          fontWeight: "700",
                        }}
                      >
                        {t("access.pharmacy.medication_price_split_title")}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.textLight,
                          fontSize: 12,
                          lineHeight: 18,
                        }}
                      >
                        {t("access.pharmacy.medication_price_split_body", {
                          params: { count: String(lowerPriceCount) },
                        })}
                      </Text>
                    </View>
                  ) : null}

                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textFaint,
                      fontSize: 11,
                      lineHeight: 16,
                    }}
                  >
                    {t("access.pharmacy.medication_price_demo_disclaimer")}
                  </Text>
                </View>
              ) : null}

              {source !== "demo" &&
              pharmacy.delivery &&
              (pharmacy.delivery.coldChain ||
                (pharmacy.delivery.sameDayCities?.length ?? 0) > 0 ||
                pharmacy.delivery.overnightNationwide ||
                pharmacy.delivery.notes) ? (
                <View style={{ gap: 10 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textMid,
                      fontSize: 13,
                      fontWeight: "600",
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("access.pharmacy.delivery_section_title")}
                  </Text>
                  {pharmacy.delivery.overnightNationwide ? (
                    <ContactRow
                      icon={Truck}
                      label={t("access.pharmacy.delivery_overnight_nationwide")}
                    />
                  ) : null}
                  {(pharmacy.delivery.sameDayCities?.length ?? 0) > 0 ? (
                    <ContactRow
                      icon={Truck}
                      label={t("access.pharmacy.delivery_same_day", {
                        params: {
                          cities: pharmacy.delivery.sameDayCities!.join(", "),
                        },
                      })}
                    />
                  ) : null}
                  {pharmacy.delivery.coldChain ? (
                    <ContactRow
                      icon={Snowflake}
                      label={t("access.pharmacy.delivery_cold_chain")}
                    />
                  ) : null}
                  {pharmacy.delivery.notes ? (
                    <ContactRow icon={Truck} label={pharmacy.delivery.notes} />
                  ) : null}
                </View>
              ) : null}

              {source !== "demo" &&
              pharmacy.publishedPrices &&
              pharmacy.publishedPrices.items.length > 0 &&
              matchedMedications.length === 0 ? (
                <View style={{ gap: 12 }}>
                  <View style={{ gap: 5 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textMid,
                        fontSize: 13,
                        fontWeight: "600",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("access.pharmacy.published_prices_section_title")}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 13,
                        lineHeight: 19,
                      }}
                    >
                      {t("access.pharmacy.published_prices_body", {
                        params: {
                          date: new Date(
                            pharmacy.publishedPrices.verifiedOn,
                          ).toLocaleDateString(locale, { dateStyle: "long" }),
                        },
                      })}
                    </Text>
                  </View>
                  <View style={{ gap: 8 }}>
                    {pharmacy.publishedPrices.items.map((item) => (
                      <View
                        key={item.medication}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                          padding: 12,
                          borderRadius: 14,
                          backgroundColor: "rgba(139, 111, 94, 0.06)",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            flex: 1,
                            color: EMBER.colors.text,
                            fontSize: 14,
                            fontWeight: "600",
                          }}
                        >
                          {item.medication}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            color: EMBER.colors.text,
                            fontSize: 16,
                            fontWeight: "800",
                          }}
                        >
                          {`$${item.price.toLocaleString("en-US", {
                            minimumFractionDigits: item.price % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2,
                          })}`}
                        </Text>
                      </View>
                    ))}
                  </View>
                  <Pressable
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "access.pharmacy.published_prices_view_source",
                    )}
                    onPress={() => {
                      capturePress(
                        pressLabel(
                          t("access.pharmacy.published_prices_view_source"),
                        ),
                        "access.pharmacy.published_prices_view_source",
                      );
                      void Linking.openURL(pharmacy.publishedPrices!.sourceUrl);
                    }}
                    style={{ paddingVertical: 4 }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <ExternalLink size={14} color={EMBER.colors.warm} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.warm,
                          fontSize: 13,
                          fontWeight: "600",
                        }}
                      >
                        {t("access.pharmacy.published_prices_view_source")}
                      </Text>
                    </View>
                  </Pressable>
                </View>
              ) : null}

              {source !== "demo" &&
              pharmacy.publishedPrices &&
              matchedMedications.length > 0 ? (
                <View style={{ gap: 12 }}>
                  <View style={{ gap: 5 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textMid,
                        fontSize: 13,
                        fontWeight: "600",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("access.pharmacy.prescription_estimate_title", {
                        params: { pharmacy: pharmacy.name },
                      })}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 13,
                        lineHeight: 19,
                      }}
                    >
                      {t("access.pharmacy.prescription_estimate_body", {
                        params: { pharmacy: pharmacy.name },
                      })}
                    </Text>
                  </View>
                  <View style={{ gap: 8 }}>
                    {matchedMedications.map((m) => (
                      <View
                        key={m.key}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                          padding: 12,
                          borderRadius: 14,
                          backgroundColor: "rgba(139, 111, 94, 0.06)",
                        }}
                      >
                        <View style={{ flex: 1, gap: 2 }}>
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.text,
                              fontSize: 14,
                              fontWeight: "700",
                            }}
                          >
                            {m.label}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.textLight,
                              fontSize: 11,
                            }}
                          >
                            {m.matchedPrice!.medication}
                          </Text>
                        </View>
                        <Text
                          className="font-dm-sans"
                          style={{
                            color: EMBER.colors.text,
                            fontSize: 16,
                            fontWeight: "800",
                          }}
                        >
                          {`$${m.matchedPrice!.price.toLocaleString("en-US", {
                            minimumFractionDigits:
                              m.matchedPrice!.price % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2,
                          })}`}
                        </Text>
                      </View>
                    ))}
                  </View>
                  <View
                    style={{
                      gap: 5,
                      padding: 14,
                      borderRadius: 16,
                      backgroundColor: "rgba(139, 111, 94, 0.08)",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.warm,
                        fontSize: 11,
                        fontWeight: "700",
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      {t(
                        "access.pharmacy.prescription_estimate_starting_from",
                        {
                          params: {
                            amount: `$${publishedPriceTotal.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits:
                                  publishedPriceTotal % 1 === 0 ? 0 : 2,
                                maximumFractionDigits: 2,
                              },
                            )}`,
                          },
                        },
                      )}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 12,
                        lineHeight: 18,
                      }}
                    >
                      {t("access.pharmacy.prescription_estimate_disclaimer")}
                    </Text>
                  </View>
                  <Pressable
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "access.pharmacy.published_prices_view_source",
                    )}
                    onPress={() => {
                      capturePress(
                        pressLabel(
                          t("access.pharmacy.published_prices_view_source"),
                        ),
                        "access.pharmacy.published_prices_view_source",
                      );
                      void Linking.openURL(pharmacy.publishedPrices!.sourceUrl);
                    }}
                    style={{ paddingVertical: 4 }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <ExternalLink size={14} color={EMBER.colors.warm} />
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.warm,
                          fontSize: 13,
                          fontWeight: "600",
                        }}
                      >
                        {t("access.pharmacy.published_prices_view_source")}
                      </Text>
                    </View>
                  </Pressable>
                </View>
              ) : null}

              {pharmacy.quoteUrl || pharmacy.sourceUrl || pharmacy.website ? (
                <WarmButton
                  title={t(
                    pharmacy.quoteType === "insurance"
                      ? "access.pharmacy.open_insurance_source"
                      : "access.pharmacy.open_quote_source",
                  )}
                  onPress={openQuoteSource}
                />
              ) : null}
            </View>
          )}
        </ScrollView>
      </GlassScreenLayout>
    </Animated.View>
  );
}
